import { WebViewerInstance } from '@pdftron/webviewer';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  temporarilyHideSuggestionAnnotations,
  restoreSuggestionAnnotations,
} from 'modules/common/viewer/pdfViewer/annotationCommands/suggestionUtils';
import { applyRedactionsWithoutAttachments } from 'modules/common/viewer/pdfViewer/utils';

import { applyRedactions } from '../data/actions';
import { getRedactFileRequest } from '../data/reducer';

type UseRedactFileOnRequestProps = {
  instance: WebViewerInstance | null;
  url: string | null;
};

/**
 * When Redux `redactFileRequest` is set (after the user confirms in Apply Redactions),
 * runs PDFTron `applyRedactions`, exports a redacted blob without attachments, dispatches
 * `applyRedactions.redactFile`, reloads the current document URL, and restores suggestion
 * annotations that were temporarily hidden during the operation.
 *
 * `getRedactFileRequest` selector:
 * - Defined in `../data/reducer.ts`
 * - Consumed here to drive the export pipeline
 * - Also mapped in `ApplyRedactionsModal.js` via `connect` — passed as `redactFileRequest`
 *   for the Save button `loading` state while export runs
 */
export const useRedactFileOnRequest = ({
  instance,
  url,
}: UseRedactFileOnRequestProps) => {
  const dispatch = useDispatch();
  const redactFileRequest = useSelector(getRedactFileRequest);

  useEffect(() => {
    if (instance && redactFileRequest && url) {
      const annotationManager = instance.Core.annotationManager;

      const { stateMap, suggestionAnnotations } =
        temporarilyHideSuggestionAnnotations(annotationManager);

      annotationManager
        .applyRedactions()
        .then(async () => {
          const redactedBlob = await applyRedactionsWithoutAttachments({
            instance,
          });
          dispatch(applyRedactions.redactFile({ redactedBlob }));
          instance.UI.loadDocument(url);
        })
        .finally(() => {
          restoreSuggestionAnnotations(
            annotationManager,
            suggestionAnnotations,
            stateMap,
          );
        });
    }
  }, [instance, dispatch, redactFileRequest, url]);
};
